import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface TableStatus {
  table: {
    id: string;
    name: string;
    capacity: number;
    isActive?: boolean;
    isBookable?: boolean;
  };
  isBooked: boolean;
  reservation?: {
    name: string;
    persons: number;
  };
}

interface CombinedTableStatus {
  combinedTable: {
    id: string;
    name: string;
    totalCapacity: number;
    isActive?: boolean;
  };
  isBooked: boolean;
  reservation?: {
    name: string;
    persons: number;
  };
}

interface CompactTableStatusViewProps {
  tablesStatus: TableStatus[];
  combinedTablesStatus: CombinedTableStatus[];
}

export const CompactTableStatusView = ({
  tablesStatus,
  combinedTablesStatus,
}: CompactTableStatusViewProps) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-3">
      {/* Tables individuelles */}
      {tablesStatus.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground mb-1.5">Tables individuelles</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-1.5">
            {tablesStatus.map(({ table, isBooked, reservation }) => (
              <TooltipProvider key={table.id}>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "p-1.5 rounded border-l-[3px] transition-all cursor-default",
                        "bg-muted/40 hover:bg-muted/60",
                        isBooked
                          ? "border-l-red-500"
                          : "border-l-green-500"
                      )}
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-[11px] leading-tight truncate w-full">{table.name}</span>
                        <span className="text-[9px] text-muted-foreground">{table.capacity}p</span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <div className="space-y-1">
                      <div className="font-semibold">{table.name}</div>
                      <div>Capacité: {table.capacity} pers.</div>
                      <Badge
                        variant={isBooked ? "destructive" : "default"}
                        className={cn(
                          "text-[10px]",
                          isBooked ? "bg-red-500 text-white" : "bg-green-500 text-white"
                        )}
                      >
                        {isBooked ? "Réservée" : "Libre"}
                      </Badge>
                      {isBooked && reservation && (
                        <div className="pt-1 border-t border-border/50">
                          <span className="font-medium">{reservation.name}</span>
                          <span className="text-muted-foreground ml-1">({reservation.persons}p)</span>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      )}

      {/* Tables combinées */}
      {combinedTablesStatus.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground mb-1.5">Tables combinées</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-1.5">
            {combinedTablesStatus.map(({ combinedTable, isBooked, reservation }) => (
              <TooltipProvider key={combinedTable.id}>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "p-1.5 rounded border-l-[3px] transition-all cursor-default",
                        "bg-muted/40 hover:bg-muted/60",
                        isBooked
                          ? "border-l-red-500"
                          : "border-l-green-500"
                      )}
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-[10px] leading-tight truncate w-full">{combinedTable.name}</span>
                        <span className="text-[9px] text-muted-foreground">{combinedTable.totalCapacity}p</span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <div className="space-y-1">
                      <div className="font-semibold">{combinedTable.name}</div>
                      <div>Capacité: {combinedTable.totalCapacity} pers.</div>
                      <Badge
                        variant={isBooked ? "destructive" : "default"}
                        className={cn(
                          "text-[10px]",
                          isBooked ? "bg-red-500 text-white" : "bg-green-500 text-white"
                        )}
                      >
                        {isBooked ? "Réservée" : "Libre"}
                      </Badge>
                      {isBooked && reservation && (
                        <div className="pt-1 border-t border-border/50">
                          <span className="font-medium">{reservation.name}</span>
                          <span className="text-muted-foreground ml-1">({reservation.persons}p)</span>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
